import { useState, useRef } from "react";
import { Box, Button, IconButton, Popover } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ShareOutlinedIcon from "@material-ui/icons/ShareOutlined";
import LinkedInIcon from "@material-ui/icons/LinkedIn";
import TwitterIcon from "@material-ui/icons/Twitter";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import FacebookIcon from "@material-ui/icons/Facebook";
import dayjs from "dayjs";

const useStyles = makeStyles(() => ({
  shareBtn: {
    textTransform: "none",
    fontSize: 11,
    fontWeight: 500,
    padding: "2px 10px",
    minWidth: "auto",
    color: "#0C3450",
    border: "1px solid #0C3450",
    borderRadius: 4,
    lineHeight: 1.6,
    "&:hover": { backgroundColor: "rgba(12,52,80,0.06)" },
  },
  popoverRoot: {
    zIndex: "9999999 !important",
  },
  popover: { display: "flex", gap: "4px", padding: "8px 12px" },
  socialIcon: { padding: 6, "&:hover": { backgroundColor: "#f5f5f5" } },
}));

const getShareText = (badge) => {
  const date = badge?.awardedDate
    ? dayjs(badge.awardedDate).format("DD-MMM-YYYY")
    : dayjs().format("DD-MMM-YYYY");
  return `I just earned the "${badge?.title}" badge on ${date}! 🏆`;
};

const handleShare = async (platform, badge, onClose) => {
  const text = getShareText(badge);
  const badgeImageUrl = badge?.badgeUrl || '';

  let shareUrl = '';
  switch (platform) {
    case 'linkedin':
      shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(badgeImageUrl || window.location.origin)}&title=${encodeURIComponent(text)}&summary=${encodeURIComponent(text)}`;
      break;
    case 'twitter': {
      let imageCopied = false;
      if (badgeImageUrl) {
        try {
          const imageKey = badgeImageUrl.replace(/^https?:\/\/[^/]+\//, '');
          const proxyUrl = `${process.env.REACT_APP_BACKEND_URL}/badge/image/${imageKey}`;
          const response = await fetch(proxyUrl);
          const blob = await response.blob();
          const pngBlob = new Blob([blob], { type: 'image/png' });
          await navigator.clipboard.write([
            new ClipboardItem({ 'image/png': pngBlob })
          ]);
          imageCopied = true;
        } catch (e) {
          console.error('Clipboard copy failed:', e);
        }
      }
      const tweetText = imageCopied
        ? text + '\n\n📌 Badge image copied to clipboard! Press Ctrl+V / ⌘+V to paste.'
        : text;
      shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
      break;
    }
    case 'whatsapp':
      shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(text + '\n' + badgeImageUrl)}`;
      break;
    case 'facebook':
      shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(badgeImageUrl || window.location.origin)}&quote=${encodeURIComponent(text)}`;
      break;
    default:
      break;
  }
  if (shareUrl) window.open(shareUrl, '_blank', 'noopener,noreferrer');
  onClose();
};

export default function BadgeSharePopover({ badge }) {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const btnRef = useRef(null);

  if (!badge?.title) return null;

  const open = Boolean(anchorEl);
  const onClose = () => setAnchorEl(null);

  return (
    <>
      <Button
        ref={btnRef}
        className={classes.shareBtn}
        variant="outlined"
        size="small"
        startIcon={<ShareOutlinedIcon style={{ fontSize: 12 }} />}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setAnchorEl(e.currentTarget);
        }}
      >
        Share
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        className={classes.popoverRoot}
        onClick={(e) => e.stopPropagation()}
      >
        <Box className={classes.popover}>
          <IconButton className={classes.socialIcon} onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleShare("linkedin", badge, onClose); }} size="small">
            <LinkedInIcon style={{ fontSize: 20, color: "#0A66C2" }} />
          </IconButton>
          <IconButton className={classes.socialIcon} onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleShare("twitter", badge, onClose); }} size="small">
            <TwitterIcon style={{ fontSize: 20, color: "#1DA1F2" }} />
          </IconButton>
          <IconButton className={classes.socialIcon} onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleShare("whatsapp", badge, onClose); }} size="small">
            <WhatsAppIcon style={{ fontSize: 20, color: "#25D366" }} />
          </IconButton>
          <IconButton className={classes.socialIcon} onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleShare("facebook", badge, onClose); }} size="small">
            <FacebookIcon style={{ fontSize: 20, color: "#1877F2" }} />
          </IconButton>
        </Box>
      </Popover>
    </>
  );
}
